import React from 'react';
import Link from 'next/link';
import { capitalizeFirstLetter, cleanString } from '@/utils';
import { PrismicDocument } from '@prismicio/client';
import { ArrowRightIcon } from '@/assets';

interface IBreadcrumbsProps {
  showHome?: boolean;
  page?: PrismicDocument;
  className?: string;
  classNameLink?: string;
  colorIcon?: string;
}

interface IBreadcrumbItemProps {
  children: React.ReactNode;
}

interface IBreadcrumbPathItem {
  id: string;
  url: string;
  page_name: string;
}

const BreadcrumbItem = ({ children, ...props }: IBreadcrumbItemProps) => {
  return <li {...props}>{children}</li>;
};

const isPagePrismicDocument = (
  page: object | undefined,
): page is PrismicDocument => {
  return typeof page === 'object' && page !== null && 'data' in page;
};

const getSliceTypeAttributes = (page?: PrismicDocument) => {
  if (isPagePrismicDocument(page)) {
    const sliceType = page.data?.slices?.[0]?.slice_type;
    if (sliceType && sliceType === 'hero_background') {
      return {
        sliceClassName: 'absolute top-1/3 left-1/4 md:block hidden',
      };
    }
  }
  return {
    sliceClassName:
      'absolute top-28 left-1/2 -translate-x-1/2 z-20 w-[90vw] max-w-sm sm:max-w-md',
  };
};

const breadcrumbPaths = (page?: PrismicDocument): IBreadcrumbPathItem[] => {
  if (page?.type === 'blog') {
    return [
      ...breadcrumbBlogPaths(page),
      {
        id: 'blog',
        url: '/blog',
        page_name: 'Blog',
      },
    ];
  }

  return breadcrumbPagePaths(page);
};

const breadcrumbPagePaths = (
  obj?: PrismicDocument,
  paths: IBreadcrumbPathItem[] = [],
): IBreadcrumbPathItem[] => {
  if (obj) {
    paths.push({
      id: obj.id,
      url: obj.url ?? '',
      page_name: obj.data?.page_name ?? obj.uid ?? '',
    });
    if (obj.data?.parent_page?.id) {
      breadcrumbPagePaths(obj.data.parent_page, paths);
    }
  }
  return paths;
};

const breadcrumbBlogPaths = (
  page?: PrismicDocument,
  paths: IBreadcrumbPathItem[] = [],
): IBreadcrumbPathItem[] => {
  if (page) {
    if (page.type === 'blog') {
      const url = `/blog/${page.uid}`;
      paths.push({
        id: page.id,
        url,
        page_name: page.data?.meta_title ?? page.uid,
      });
    }

    if (page.data?.category?.id) {
      breadcrumbBlogPaths(page.data.category, paths);
    }
  }
  return paths;
};

const getDisplayName = (item: IBreadcrumbPathItem) => {
  const pageName =
    typeof item?.page_name === 'string'
      ? capitalizeFirstLetter(item.page_name)
      : item.page_name;

  return typeof pageName === 'string' ? cleanString(pageName) : pageName;
};

export const Breadcrumb = ({
  showHome = true,
  page,
  className,
  classNameLink = 'ml-4 font-light text-[#05F273] hover:text-green-400',
  colorIcon = '#05F273',
}: IBreadcrumbsProps) => {
  const { sliceClassName } = getSliceTypeAttributes(page);
  const breadcrumbPathItems = breadcrumbPaths(page).reverse();

  const classNameLinkBold =
    classNameLink.replace('font-light', '') + ' font-bold';

  return (
    <section className={className ?? sliceClassName}>
      <nav className="flex justify-center" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-4">
          {showHome && (
            <BreadcrumbItem>
              <div>
                <Link href="/">
                  <span className="text-[#05F273]">Home</span>
                </Link>
              </div>
            </BreadcrumbItem>
          )}
          {breadcrumbPathItems.map((item, index) => (
            <BreadcrumbItem key={item.id}>
              <div className="flex items-center">
                {(index !== 0 || showHome) && (
                  <span className="text-[#05F273]">
                    <ArrowRightIcon fill={colorIcon} />
                  </span>
                )}
                {index === breadcrumbPathItems.length - 1 && (
                  <Link href={`${item.url}`} className={classNameLinkBold}>
                    {getDisplayName(item)}
                  </Link>
                )}
                {index !== breadcrumbPathItems.length - 1 && (
                  <Link href={`${item.url}`} className={classNameLink}>
                    {getDisplayName(item)}
                  </Link>
                )}
              </div>
            </BreadcrumbItem>
          ))}
        </ol>
      </nav>
    </section>
  );
};
